import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  TextField,
  Button,
  Paper,
  Box,
  Typography,
  MenuItem,
  Switch,
  FormControlLabel,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Chip,
  Tooltip,
  Autocomplete,
  IconButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Layout from "./Layout";
import { useAuth } from "../context/AuthProvider";
// A subcomponent for handling subdirectory tags input
const SubDirectoryTagInput = ({
  includeTags,
  setIncludeTags,
  excludeTags,
  setExcludeTags,
}) => {
  const [includeValue, setIncludeValue] = useState("");
  const [excludeValue, setExcludeValue] = useState("");

  const handleAddTag = (value, tags, setTags, clearInput) => {
    const trimmed = value.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
      clearInput("");
    }
  };

  const handleDeleteTag = (tag, tags, setTags) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const showInclude = excludeTags.length === 0 && excludeValue.trim() === "";
  const showExclude = includeTags.length === 0 && includeValue.trim() === "";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mb: 2 }}>
      {/* Include Section */}
      {showInclude && (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mr: 1 }}>
              Include Files
            </Typography>
            <Tooltip
              title="Only the selected files and folders will be used for scanning."
              arrow
              PopperProps={{
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [10, 10],
                    },
                  },
                ],
              }}
            >
              <IconButton size="small" sx={{ p: 0.5 }}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {includeTags.map((tag, idx) => (
              <Chip
                key={idx}
                label={tag}
                onDelete={() =>
                  handleDeleteTag(tag, includeTags, setIncludeTags)
                }
                color="primary"
              />
            ))}
            <TextField
              value={includeValue}
              onChange={(e) => setIncludeValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ",") {
                  e.preventDefault();
                  handleAddTag(
                    includeValue,
                    includeTags,
                    setIncludeTags,
                    setIncludeValue
                  );
                }
              }}
              placeholder="Add include path and press Enter or ','"
              size="small"
              sx={{ minWidth: 300 }}
            />
          </Box>
        </Box>
      )}

      {/* Exclude Section */}
      {showExclude && (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mr: 1 }}>
              Exclude Files
            </Typography>
            <Tooltip
              title="Only the selected files and folders will not be used for scanning."
              arrow
              PopperProps={{
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [10, 10],
                    },
                  },
                ],
              }}
            >
              <IconButton size="small" sx={{ p: 0.5 }}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {excludeTags.map((tag, idx) => (
              <Chip
                key={idx}
                label={tag}
                onDelete={() =>
                  handleDeleteTag(tag, excludeTags, setExcludeTags)
                }
                color="error"
              />
            ))}
            <TextField
              value={excludeValue}
              onChange={(e) => setExcludeValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ",") {
                  e.preventDefault();
                  handleAddTag(
                    excludeValue,
                    excludeTags,
                    setExcludeTags,
                    setExcludeValue
                  );
                }
              }}
              placeholder="Add exclude path and press Enter or ','"
              size="small"
              sx={{ minWidth: 300 }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};
const SrcDirectoryTagInput = ({ srcDirectoryTags, setSrcDirectoryTags }) => {
  const [srcValue, setSrcValue] = useState("");

  const handleInputChangesrc = (e) => setSrcValue(e.target.value);

  const handleKeyDownsrc = (e) => {
    if (e.key === "," || e.key === "Enter") {
      e.preventDefault();
      const trimmedValue = srcValue.trim();
      if (trimmedValue && !srcDirectoryTags.includes(trimmedValue)) {
        setSrcDirectoryTags([...srcDirectoryTags, trimmedValue]);
        setSrcValue("");
      }
    }
  };

  const handleDeletesrc = (tagToDelete) => {
    setSrcDirectoryTags(srcDirectoryTags.filter((tag) => tag !== tagToDelete));
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
      {srcDirectoryTags.map((tag, index) => (
        <Chip
          key={index}
          label={tag}
          onDelete={() => handleDeletesrc(tag)}
          color="primary"
        />
      ))}
      <TextField
        value={srcValue}
        onChange={handleInputChangesrc}
        onKeyDown={handleKeyDownsrc}
        placeholder="Add Source directory and press Enter or ','"
        variant="outlined"
        size="small"
        sx={{ minWidth: 300, fontSize: "2px" }}
      />
    </Box>
  );
};
const IncludeDirectoryTagInput = ({
  includeDirectoryTags,
  setIncludeDirectoryTags,
}) => {
  const [includedirValue, setIncludedirValue] = useState("");

  const handleInputChangeinclude = (e) => setIncludedirValue(e.target.value);

  const handleKeyDowninclude = (e) => {
    if (e.key === "," || e.key === "Enter") {
      e.preventDefault();
      const trimmedValue = includedirValue.trim();
      if (trimmedValue && !includeDirectoryTags.includes(trimmedValue)) {
        setIncludeDirectoryTags([...includeDirectoryTags, trimmedValue]);
        setIncludedirValue("");
      }
    }
  };

  const handleDeleteinclude = (tagToDelete) => {
    setIncludeDirectoryTags(
      includeDirectoryTags.filter((tag) => tag !== tagToDelete)
    );
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
      {includeDirectoryTags.map((tag, index) => (
        <Chip
          key={index}
          label={tag}
          onDelete={() => handleDeleteinclude(tag)}
          color="primary"
        />
      ))}
      <TextField
        value={includedirValue}
        onChange={handleInputChangeinclude}
        onKeyDown={handleKeyDowninclude}
        placeholder="Add Include directory and press Enter or ','"
        variant="outlined"
        size="small"
        sx={{ minWidth: 300, fontSize: "2px" }}
      />
    </Box>
  );
};

const EditProject = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [isPrivateRepo, setIsPrivateRepo] = useState(false);
  const [useSubDirectory, setUseSubDirectory] = useState(false);
  const [subDirectoryTags, setSubDirectoryTags] = useState([]);
  const [includeTags, setIncludeTags] = useState([]);
  const [excludeTags, setExcludeTags] = useState([]);
  const [useSrcDirectory, setUseSrcDirectory] = useState(false);
  const [srcDirectoryTags, setSrcDirectoryTags] = useState([]);
  const [useIncludeDirectory, setUseIncludeDirectory] = useState(false);
  const [includeDirectoryTags, setIncludeDirectoryTags] = useState([]);
  const { authToken, planId } = useAuth();
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("info");
  const [error, setError] = useState(null);
  const [organizationType, setOrganizationType] = useState(null);
  const [hasLoadedFromBackend, setHasLoadedFromBackend] = useState(false);
  const [hasInitialized, setHasInitialized] = useState({
    buildTool: false,
    staticAnalysisTools: false,
    unitTestingTools: false,
    memoryTestingTools: false,
    cybersecurityAnalysisTools: false,
    fossComplianceTools: false,
  });
  
  const token = localStorage.getItem("access_token");
  localStorage.setItem("invoked_project_id", projectId);
  const [project, setProject] = useState({
    name: "",
    description: "",
    repoProvider: "github",
    codeLanguage: "",
    src_dir: "",
    include_dir: "",
    msu_definition: "",
    clientRepoUrl: "",
    mainFileName: "",
    updated_timestamp: "",
  });

  const [configurations, setConfigurations] = useState({
    // version: '',
    latestConfig: {
      buildTool: {
        tool_id: "",
        toolName: "",
      },
      staticAnalysisTools: [],
      unitTestingTools: [],
      memoryTestingTools: [],
      cybersecurityAnalysisTools: [],
      fossComplianceTools: []
    },
  });

  const [tools, setTools] = useState({
    buildTool: [],
    staticAnalysisTools: [],
    unitTestingTools: [],
    memoryTestingTools: [],
    cybersecurityAnalysisTools: [],
    fossComplianceTools: []
  });

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  const msuOptions = [
    "Definition A",
    "Definition B",
    "Definition C",
    "Definition D",
    "Definition E",
    "Definition F",
    "Definition G",
    "Definition H",
  ];

  const handleMsuChange = (event, newValue) => {
    handleChange({
      target: {
        name: "msu_definition",
        value: newValue,
      },
    });
  };

  const handlePermitAccess = () => {
    const project_id = projectId;
    // First, invoke /invoke-setup-project-repository to mark project as "invoked"
    fetch(
      `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/github/invoke-setup-project-repository`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ project_id }),
      }
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setAlertMessage(data.message);
          setAlertSeverity("info");
          // Prepare state with projectId and returnUrl for GitHub installation redirection
          const stateData = encodeURIComponent(
            JSON.stringify({ project_id, returnUrl: window.location.href })
          );
          const githubInstallUrl = `https://github.com/apps/${process.env.REACT_APP_GITHUB_APP_SLUG}/installations/new?state=${stateData}`;
          window.open(githubInstallUrl, "_blank");
        } else {
          setAlertMessage("Failed to mark project as invoked. Please retry.");
          setAlertSeverity("error");
        }
      })
      .catch((err) => {
        console.error("Error invoking setup-project-repository:", err);
        setAlertMessage("Error invoking setup. Please retry.");
        setAlertSeverity("error");
      });
  };

  useEffect(() => {
    // Fetch organization profile to determine organizationType
    fetch(`${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/profile`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        const orgType = data.organization?.organization_type;
        setOrganizationType(orgType);
      })
      .catch((err) => {
        console.error("Error fetching organization profile:", err);
      });
  }, [token]);
  

  //useEffect() => {
    const fetchProjectDetails = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/projects/${projectId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      console.log("Fetched project data:", data);
      console.log("Config from backend:", data.latestConfig || data.configurations);

      if (data) {
        const config = data.latestConfig || data.configurations || {};
        
        setProject({
          name: data.name || "",
          description: data.description || "",
          updated_timestamp: data.updated_timestamp || "",
          codeLanguage: data.codeLanguage || "",
          msu_definition: data.msu_definition || "",
          repoDirectory: data.repoDirectory || "",
          sourceFilesAndSubDirectories: data.sourceFilesAndSubDirectories || {
            include: [],
            exclude: [],
          },
          repoProvider: data.repoProvider || "",
          clientRepoUrl: data.clientRepoUrl || data.repoUrl || "",
          mainFileName: data.mainFileName || "",
          outputFileName: data.outputFileName || "",
          gitHubAppDetails: data.gitHubAppDetails || {},
        });

        setUseSubDirectory(
          data.sourceFilesAndSubDirectories &&
          (data.sourceFilesAndSubDirectories.include?.length > 0 ||
            data.sourceFilesAndSubDirectories.exclude?.length > 0)
        );

        setIncludeTags(data.sourceFilesAndSubDirectories?.include || []);
        setExcludeTags(data.sourceFilesAndSubDirectories?.exclude || []);

        setUseSrcDirectory(
          Array.isArray(data.src_dir) && data.src_dir.length > 0
        );
        setSrcDirectoryTags(data.src_dir || []);

        setUseIncludeDirectory(
          Array.isArray(data.include_dir) && data.include_dir.length > 0
        );
        setIncludeDirectoryTags(data.include_dir || []);

        setConfigurations({
          latestConfig: {
            buildTool: {
              toolName: config.buildTool?.toolName || "",
              tool_id: config.buildTool?.tool_id || "",
            },
            staticAnalysisTools: (config.staticAnalysisTools || []).map(
              (tool) => ({
                toolName: tool?.toolName || "",
                tool_id: tool?.tool_id || "",
              })
            ),
            unitTestingTools: (config.unitTestingTools || []).map((tool) => ({
              toolName: tool?.toolName || "",
              tool_id: tool?.tool_id || "",
            })),
            memoryTestingTools: (config.memoryTestingTools || []).map(
              (tool) => ({
                toolName: tool?.toolName || "",
                tool_id: tool?.tool_id || "",
              })
            ),
            cybersecurityAnalysisTools: (
              config.cybersecurityAnalysisTools || []).map((tool) => ({
              toolName: tool?.toolName || "",
              tool_id: tool?.tool_id || "",
            })),
            fossComplianceTools: (
              config.fossComplianceTools || []).map((tool) => ({
              toolName: tool?.toolName || "",
              tool_id: tool?.tool_id || "",
            })),
          },
        });

        // SET hasInitialized based on backend data
        // SET hasInitialized based on backend data
        // Mark as initialized if we got data from backend (even if empty/none was selected)
        setHasInitialized({
          buildTool: true, // Always true after loading from backend
          staticAnalysisTools: true,
          unitTestingTools: true,
          memoryTestingTools: true,
          cybersecurityAnalysisTools: true,
          fossComplianceTools: true,
        });
        console.log("Set hasInitialized:", {
          buildTool: !!config.buildTool?.tool_id,
          staticAnalysisTools: (config.staticAnalysisTools || []).length > 0,
          unitTestingTools: (config.unitTestingTools || []).length > 0,
          memoryTestingTools: (config.memoryTestingTools || []).length > 0,
          cybersecurityAnalysisTools: (config.cybersecurityAnalysisTools || []).length > 0,
          fossComplianceTools: (config.fossComplianceTools || []).length > 0,
        });
        
        // Mark that we've loaded data from backend
        setHasLoadedFromBackend(true);
      }
    } catch (err) {
      console.error("Error fetching project details:", err);
      setError("Error fetching project details. Please try again.");
    }
  };

  const fetchTools = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/tools-by-plan?plan_id=${planId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch tools");
      const data = await response.json();
      
      const buildTool = data.filter((tool) => tool.toolType === "buildTool");
      const staticAnalysisTools = data.filter(
        (tool) => tool.toolType === "staticAnalysisTool"
      );
      const unitTestingTools = data.filter(
        (tool) => tool.toolType === "unitTestingTool"
      );
      const memoryTestingTools = data.filter(
        (tool) => tool.toolType === "memoryTestingTool"
      );
      const cybersecurityAnalysisTools = data.filter(
        (tool) => tool.toolType === "cyberSecurityAnalysisTool"
      );
      const fossComplianceTools = data.filter(
        (tool) => tool.toolType === "fossComplianceTool"
      );

      setTools({
        buildTool,
        staticAnalysisTools,
        unitTestingTools,
        memoryTestingTools,
        cybersecurityAnalysisTools,
        fossComplianceTools
      });
    } catch (err) {
      setError("Error loading tools. Please try again later.");
    }
  };

  // useEffect #1: Fetch organization profile
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/profile`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        const orgType = data.organization?.organization_type;
        setOrganizationType(orgType);
      })
      .catch((err) => {
        console.error("Error fetching organization profile:", err);
      });
  }, [token]);

  // useEffect #2: Initialize data (fetch tools first, then project details)
  useEffect(() => {
    const initializeData = async () => {
      await fetchTools();
      await fetchProjectDetails();
    };
    
    initializeData();
  }, [projectId, token, planId]);

  // useEffect #3: Auto-initialization for new projects only
  // useEffect #3: Auto-initialization for new projects only
  useEffect(() => {
    console.log("Auto-init effect running. hasInitialized:", hasInitialized);
    console.log("Current configurations:", configurations.latestConfig);
    const updatedConfig = {};

    if (
      tools.staticAnalysisTools.length > 0 &&
      !hasInitialized.staticAnalysisTools &&
      configurations.latestConfig.staticAnalysisTools.length === 0
    ) {
      updatedConfig.staticAnalysisTools = tools.staticAnalysisTools.map(
        (tool) => ({
          tool_id: tool.tool_id,
          toolName: tool.toolName,
        })
      );
    }

    if (
      tools.memoryTestingTools.length > 0 &&
      !hasInitialized.memoryTestingTools &&
      configurations.latestConfig.memoryTestingTools.length === 0
    ) {
      updatedConfig.memoryTestingTools = tools.memoryTestingTools.map(
        (tool) => ({
          tool_id: tool.tool_id,
          toolName: tool.toolName,
        })
      );
    }

    if (
      tools.unitTestingTools.length > 0 && 
      !hasInitialized.unitTestingTools &&
      configurations.latestConfig.unitTestingTools.length === 0
    ) {
      updatedConfig.unitTestingTools = tools.unitTestingTools.map((tool) => ({
        tool_id: tool.tool_id,
        toolName: tool.toolName,
      }));
    }

    if (
      tools.cybersecurityAnalysisTools.length > 0 &&
      !hasInitialized.cybersecurityAnalysisTools &&
      configurations.latestConfig.cybersecurityAnalysisTools.length === 0
    ) {
      updatedConfig.cybersecurityAnalysisTools =
        tools.cybersecurityAnalysisTools.map((tool) => ({
          tool_id: tool.tool_id,
          toolName: tool.toolName,
        }));
    }

    if (
      tools.fossComplianceTools.length > 0 &&
      !hasInitialized.fossComplianceTools &&
      configurations.latestConfig.fossComplianceTools.length === 0
    ) {
      updatedConfig.fossComplianceTools = tools.fossComplianceTools.map((tool) => ({
        tool_id: tool.tool_id,
        toolName: tool.toolName,
      }));
    }

    if (
      tools.buildTool.length > 0 && 
      !hasInitialized.buildTool &&
      !configurations.latestConfig.buildTool.tool_id
    ) {
      updatedConfig.buildTool = {
        tool_id: tools.buildTool[0].tool_id,
        toolName: tools.buildTool[0].toolName,
      };
    }

    if (Object.keys(updatedConfig).length > 0) {
      setConfigurations((prev) => ({
        ...prev,
        latestConfig: {
          ...prev.latestConfig,
          ...updatedConfig,
        },
      }));

      setHasInitialized((prev) => ({
        ...prev,
        ...(updatedConfig.buildTool && { buildTool: true }),
        ...(updatedConfig.staticAnalysisTools && { staticAnalysisTools: true }),
        ...(updatedConfig.unitTestingTools && { unitTestingTools: true }),
        ...(updatedConfig.memoryTestingTools && { memoryTestingTools: true }),
        ...(updatedConfig.cybersecurityAnalysisTools && { cybersecurityAnalysisTools: true }),
        ...(updatedConfig.fossComplianceTools && { fossComplianceTools: true }),
      }));
    }
  }, [tools, hasInitialized, configurations.latestConfig]);


  // Preselect a static analysis tool for organization type "I" if not already set
  // useEffect(() => {
  //   if (
  //     tools.staticAnalysisTools.length > 0 &&
  //     (!configurations.latestConfig.staticAnalysisTools ||
  //       configurations.latestConfig.staticAnalysisTools.length === 0)
  //   ) {
  //     const defaultTool = tools.staticAnalysisTools[0];
  //     setConfigurations((prev) => ({
  //       ...prev,
  //       latestConfig: {
  //         ...prev.latestConfig,
  //         staticAnalysisTools: [
  //           { tool_id: defaultTool.tool_id, toolName: defaultTool.toolName },
  //         ],
  //       },
  //     }));
  //   }
  // }, [
  //   organizationType,
  //   tools.staticAnalysisTools,
  //   configurations.latestConfig.staticAnalysisTools,
  // ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProject({ ...project, [name]: value });
  };

  const handleRepoProviderChange = (e) => {
    const value = e.target.value;
    setProject({ ...project, repoProvider: value, clientRepoUrl: "" });
  };

  // Handle file selection when "manual" is chosen.
  const handleFileSelection = (e) => {
    const files = e.target.files;
    // Process the selected files as needed.
    console.log("Selected files:", files);
  };

  const handleConfigChange = (e, type) => {
    const { value } = e.target;
    // console.log(type);
    if (
      type === "staticAnalysisTools" ||
      type === "unitTestingTools" ||
      type === "memoryTestingTools" ||
      type === "cybersecurityAnalysisTools" ||
      type === "fossComplianceTools"
    ) {
      const selectedTools = tools[type].filter((tool) =>
        value.includes(tool.tool_id)
      );
      setConfigurations({
        ...configurations,
        latestConfig: {
          ...configurations.latestConfig,
          [type]: selectedTools,
        },
      });
    }
    if (value === "") {
      // Clear the selected build tool
      setConfigurations({
        ...configurations,
        latestConfig: {
          ...configurations.latestConfig,
          [type]: {
            tool_id: "",
            toolName: "",
          },
        },
      });
    } else {
      const selectedTool = tools.buildTool.find(
        (tool) => tool.tool_id === value
      );
      if (selectedTool) {
        setConfigurations({
          ...configurations,
          latestConfig: {
            ...configurations.latestConfig,
            [type]: {
              tool_id: selectedTool.tool_id,
              toolName: selectedTool.toolName,
            },
          },
        });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const requestData = {
      ...project,
      mainFileName: project.mainFileName,
      sourceFilesAndSubDirectories: {
        include: includeTags,
        exclude: excludeTags,
      },
      src_dir: srcDirectoryTags,
      include_dir: includeDirectoryTags,
      latestConfig: configurations.latestConfig,
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/projects/${projectId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestData),
        }
      );

      if (response.ok) {
        console.log("✅ Project updated successfully!");
        await fetchProjectDetails();
        navigate(`/projects/${projectId}`);
      } else {
        setError("Error updating project.");
      }
      //await fetchTools();
      

    } catch (error) {
      console.error("Error updating project:", error);
      setError("Error updating project: " + error.message);
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 2 }}>
        <Paper sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              mt: 2,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="h5" gutterBottom>
              Edit Project
            </Typography>
            <Button
              variant="contained"
              className="athrv-dash-primary-button"
              sx={{
                mb: { xs: 2, sm: 1 },
                mt: { xs: 1, sm: 0 },
                alignSelf: { xs: "flex-start", sm: "auto" },
              }}
              onClick={() => navigate(`/projects`)}
            >
              Back to Project List
            </Button>
          </Box>
          {error && <Typography color="error">{error}</Typography>}

          <form onSubmit={handleSubmit}>
            <TextField
              label="Project Name"
              name="name"
              value={project.name}
              onChange={handleChange}
              required
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Description"
              name="description"
              value={project.description}
              onChange={handleChange}
              required
              multiline
              rows={4}
              fullWidth
              sx={{ mb: 2 }}
            />

            {/* Repo Provider Dropdown */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="repo-provider-label">Repo Provider</InputLabel>
              <Select
                labelId="repo-provider-label"
                label="Repo Provider"
                name="repoProvider"
                value={project.repoProvider}
                onChange={handleRepoProviderChange}
              >
                <MenuItem value="github">GitHub</MenuItem>
                <MenuItem value="bitbucket" disabled>
                  Bitbucket (Coming Soon)
                </MenuItem>
                <MenuItem value="gitlab" disabled>
                  GitLab (Coming Soon)
                </MenuItem>
                <MenuItem value="manual" disabled>
                  Manual Upload (Coming Soon)
                </MenuItem>
              </Select>
            </FormControl>

            {/* Conditionally show clientRepoUrl field or file selector */}
            {project.repoProvider !== "manual" ? (
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <TextField
                  label="Repo Url"
                  name="clientRepoUrl"
                  value={project.clientRepoUrl}
                  onChange={handleChange}
                  required
                  multiline
                  rows={1}
                  fullWidth
                  disabled={!!project.clientRepoUrl && hasLoadedFromBackend}
                  sx={{ mr: 2 }}
                />
                {project.gitHubAppDetails &&
                  project.gitHubAppDetails.githubAppInstallationId ? (
                  <>
                    <Button
                      variant="contained"
                      sx={{ whiteSpace: "nowrap" }}
                      className="athrv-dash-primary-button"
                      onClick={handlePermitAccess}
                    >
                      Update Access
                    </Button>
                    <Tooltip
                      title="You can update access"
                      placement="top"
                      arrow
                    >
                      <IconButton size="small">
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    sx={{ whiteSpace: "nowrap" }}
                    className="athrv-dash-primary-button"
                    onClick={handlePermitAccess}
                  >
                    Permit Access
                  </Button>
                )}
              </Box>
            ) : (
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  className="athrv-dash-primary-button"
                  component="label"
                >
                  Select Files & Folder
                  <input
                    type="file"
                    hidden
                    // Note: "webkitdirectory" is a non-standard attribute that works in some browsers.
                    webkitdirectory="true"
                    onChange={handleFileSelection}
                  />
                </Button>
              </Box>
            )}

            <FormControl fullWidth required sx={{ mb: 2 }}>
              <InputLabel id="code-language-label">Code Language</InputLabel>
              <Select
                labelId="code-language-label"
                label="Code Language"
                name="codeLanguage"
                value={project.codeLanguage}
                onChange={handleChange}
              >
                <MenuItem value="c">C</MenuItem>
                <MenuItem value="cpp">C++</MenuItem>
                <MenuItem value="c,cpp">C and C++</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={useSubDirectory}
                  onChange={() => setUseSubDirectory(!useSubDirectory)}
                />
              }
              label="Custom Sources"
            />
            {useSubDirectory && (
              <SubDirectoryTagInput
                includeTags={includeTags}
                setIncludeTags={setIncludeTags}
                excludeTags={excludeTags}
                setExcludeTags={setExcludeTags}
              />
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={useSrcDirectory}
                  onChange={() => setUseSrcDirectory(!useSrcDirectory)}
                />
              }
              label="Custom Sources Directrory"
            />
            {useSrcDirectory && (
              <SrcDirectoryTagInput
                srcDirectoryTags={srcDirectoryTags}
                setSrcDirectoryTags={setSrcDirectoryTags}
              />
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={useIncludeDirectory}
                  onChange={() => setUseIncludeDirectory(!useIncludeDirectory)}
                />
              }
              label="Custom Include Directory"
            />
            {useIncludeDirectory && (
              <IncludeDirectoryTagInput
                includeDirectoryTags={includeDirectoryTags}
                setIncludeDirectoryTags={setIncludeDirectoryTags}
              />
            )}
            {/* <Autocomplete
              freeSolo
              options={msuOptions}
              value={project.msu_definition || ""}
              onChange={(event, newValue) => handleMsuChange(event, newValue)}
              onInputChange={(event, newInputValue) =>
                handleMsuChange(event, newInputValue)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="MSU Definition"
                  fullWidth
                  sx={{ mb: 2 }}
                />
              )}
              ListboxProps={{
                style: {
                  maxHeight: 150,
                },
              }}
            /> */}
            <TextField
              label="Main File Full Path"
              name="mainFileName"
              value={project.mainFileName}
              onChange={handleChange}
              required
              fullWidth
              sx={{ mb: 2 }}
            />

            <TextField
              label="Created Date"
              name="created_date"
              type="date"
              value={project.created_date}
              onChange={handleChange}
              fullWidth
              sx={{ mb: 2, display: "none" }}
              disabled
            />

            <Typography variant="h6" gutterBottom>
              Tools Configuration
            </Typography>
            {tools.buildTool.length > 0 && (
              <FormControl fullWidth>
                <InputLabel id="build-tool-select-label">Build Tool</InputLabel>
                <Select
                  labelId="build-tool-select-label"
                  id="build-tool-select"
                  value={configurations.latestConfig.buildTool.tool_id || ""}
                  label="Build Tool"
                  onChange={(e) => handleConfigChange(e, "buildTool")}
                  sx={{ mb: 2 }}
                >
                  <MenuItem value="">None</MenuItem>
                  {tools.buildTool.map((tool) => (
                    <MenuItem
                      key={tool.tool_id}
                      value={tool.tool_id}
                      name={tool.toolName}
                    >
                      {tool.toolName} (v{tool.toolVersion})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {/* Multi-select for Static Analysis Tools */}
            {tools.staticAnalysisTools.length > 0 && (
              <FormControl fullWidth sx={{ mb: 1 }}>
                <InputLabel id="static-analysis-label">
                  Static Analysis Tools
                </InputLabel>
                <Select
                  labelId="static-analysis-label"
                  id="static-analysis-tools-select"
                  multiple
                  value={
                    configurations.latestConfig.staticAnalysisTools.map(
                      (tool) => tool.tool_id
                    ) || []
                  }
                  onChange={(e) => handleConfigChange(e, "staticAnalysisTools")}
                  input={<OutlinedInput label="Static Analysis Tools" />}
                  renderValue={(selected) =>
                    selected
                      .map((value) => {
                        const tool = tools.staticAnalysisTools.find(
                          (tool) => tool.tool_id === value
                        );
                        return tool ? tool.toolName : "";
                      })
                      .join(", ")
                  }
                  MenuProps={MenuProps}
                >
                  {tools.staticAnalysisTools.map((tool) => (
                    <MenuItem
                      key={tool.tool_id}
                      value={tool.tool_id}
                      disabled={tool.immutable}
                    >
                      <Checkbox
                        checked={configurations.latestConfig.staticAnalysisTools.some(
                          (selectedTool) =>
                            selectedTool.tool_id === tool.tool_id
                        )}
                      />
                      <ListItemText
                        primary={tool.toolName}
                        secondary={`v${tool.toolVersion}`}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Unit Testing Tools Multi-select */}
            {tools.unitTestingTools.length > 0 && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="unit-testing-label">
                  Unit Testing Tools
                </InputLabel>
                <Select
                  labelId="unit-testing-label"
                  id="unit-testing-tools-select"
                  multiple
                  value={
                    configurations.latestConfig.unitTestingTools.map(
                      (tool) => tool.tool_id
                    ) || []
                  }
                  onChange={(e) => handleConfigChange(e, "unitTestingTools")}
                  input={<OutlinedInput label="Unit Testing Tools" />}
                  renderValue={(selected) =>
                    selected
                      .map((value) => {
                        const tool = tools.unitTestingTools.find(
                          (tool) => tool.tool_id === value
                        );
                        return tool ? tool.toolName : "";
                      })
                      .join(", ")
                  }
                  MenuProps={MenuProps}
                >
                  {tools.unitTestingTools.map((tool) => (
                    <MenuItem key={tool.tool_id} value={tool.tool_id}>
                      <Checkbox
                        checked={configurations.latestConfig.unitTestingTools.some(
                          (selectedTool) =>
                            selectedTool.tool_id === tool.tool_id
                        )}
                      />
                      <ListItemText
                        primary={tool.toolName}
                        secondary={`v${tool.toolVersion}`}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Memory Testing Tools Multi-select */}
            {tools.memoryTestingTools.length > 0 && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="memory-testing-label">
                  Memory Testing Tools
                </InputLabel>
                <Select
                  labelId="memory-testing-label"
                  id="memory-testing-tools-select"
                  multiple
                  value={
                    configurations.latestConfig.memoryTestingTools.map(
                      (tool) => tool.tool_id
                    ) || []
                  }
                  onChange={(e) => handleConfigChange(e, "memoryTestingTools")}
                  input={<OutlinedInput label="Memory Testing Tools" />}
                  renderValue={(selected) =>
                    selected
                      .map((value) => {
                        const tool = tools.memoryTestingTools.find(
                          (tool) => tool.tool_id === value
                        );
                        return tool ? tool.toolName : "";
                      })
                      .join(", ")
                  }
                  MenuProps={MenuProps}
                >
                  {tools.memoryTestingTools.map((tool) => (
                    <MenuItem key={tool.tool_id} value={tool.tool_id}>
                      <Checkbox
                        checked={configurations.latestConfig.memoryTestingTools.some(
                          (selectedTool) =>
                            selectedTool.tool_id === tool.tool_id
                        )}
                      />
                      <ListItemText
                        primary={tool.toolName}
                        secondary={`v${tool.toolVersion}`}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Cyber Testing Tools Multi-select */}
            {tools.cybersecurityAnalysisTools.length > 0 && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="cyber-testing-label">
                  Cyber Security Analysis Tools
                </InputLabel>
                <Select
                  labelId="cyber-testing-label"
                  id="cyber-testing-tools-select"
                  multiple
                  value={
                    configurations.latestConfig.cybersecurityAnalysisTools.map(
                      (tool) => tool.tool_id
                    ) || []
                  }
                  onChange={(e) =>
                    handleConfigChange(e, "cybersecurityAnalysisTools")
                  }
                  input={
                    <OutlinedInput label="Cyber Security Analysis Tools" />
                  }
                  renderValue={(selected) =>
                    selected
                      .map((value) => {
                        const tool = tools.cybersecurityAnalysisTools.find(
                          (tool) => tool.tool_id === value
                        );
                        return tool ? tool.toolName : "";
                      })
                      .join(", ")
                  }
                  MenuProps={MenuProps}
                >
                  {tools.cybersecurityAnalysisTools.map((tool) => (
                    <MenuItem key={tool.tool_id} value={tool.tool_id}>
                      <Checkbox
                        checked={configurations.latestConfig.cybersecurityAnalysisTools.some(
                          (selectedTool) =>
                            selectedTool.tool_id === tool.tool_id
                        )}
                      />
                      <ListItemText
                        primary={tool.toolName}
                        secondary={`v${tool.toolVersion}`}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* FOSS Compliance Tools Multi-select */}
            {tools.fossComplianceTools.length > 0 && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="cyber-testing-label">
                  Cyber Security Analysis Tools
                </InputLabel>
                <Select
                  labelId="cyber-testing-label"
                  id="cyber-testing-tools-select"
                  multiple
                  value={
                    configurations.latestConfig.fossComplianceTools.map(
                      (tool) => tool.tool_id
                    ) || []
                  }
                  onChange={(e) =>
                    handleConfigChange(e, "fossComplianceTools")
                  }
                  input={
                    <OutlinedInput label="FOSS Compliance Analysis Tools" />
                  }
                  renderValue={(selected) =>
                    selected
                      .map((value) => {
                        const tool = tools.fossComplianceTools.find(
                          (tool) => tool.tool_id === value
                        );
                        return tool ? tool.toolName : "";
                      })
                      .join(", ")
                  }
                  MenuProps={MenuProps}
                >
                  {tools.fossComplianceTools.map((tool) => (
                    <MenuItem key={tool.tool_id} value={tool.tool_id}>
                      <Checkbox
                        checked={configurations.latestConfig.fossComplianceTools.some(
                          (selectedTool) =>
                            selectedTool.tool_id === tool.tool_id
                        )}
                      />
                      <ListItemText
                        primary={tool.toolName}
                        secondary={`v${tool.toolVersion}`}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Button
              type="submit"
              variant="contained"
              className="athrv-dash-primary-button"
            >
              Save Changes
            </Button>
          </form>
        </Paper>
        <br />
        <br />
      </Box>
    </Layout>
  );
};

export default EditProject;
